// authRefresh — THE single refresh-token rotation path for the whole app.
//
// Supabase refresh tokens are SINGLE-USE: every successful POST /auth/v1/token
// rotates the refresh_token and, outside a short reuse-grace interval, replaying
// an already-consumed token trips GoTrue's reuse-detection and revokes the
// WHOLE session family. The client is then wedged — getSession() resolves the
// anon key, the anon-guard correctly refuses to run queries unauthenticated, and
// nothing issues a /rest/v1/ call (confirmed 2026-06-15: a platform-admin doing
// a tenant switch while in platform mode had the token revoked mid-flight; the
// app spun forever with zero data requests).
//
// The app used to have FIVE independent rotators that could interleave and
// replay a consumed token:
//   1. auth-js autoRefreshToken (background ticker)
//   2. sessionWatchdog (boot / visibility / heartbeat / anon-guard)
//   3. the tenant switcher (Dashboard)
//   4. SupportViewBanner "Stop"
//   5. platformView enter/exit (a SIX-attempt refresh loop)
// Paths 3-5 each rotated the token with NO coordination with 1-2 or each other.
//
// This module is the one place a MANUAL refresh happens. It is:
//   • single-flight — concurrent callers join the same in-flight refresh
//     instead of each POSTing the same one-time token,
//   • rotation-guarded — it re-reads localStorage immediately before sending
//     and ADOPTS (never replays) a token another context already rotated,
//   • bounded — the raw fetch is abort-capped so a dead socket can't hang it.
// auth-js's own autoRefresh (1) stays enabled, but it also re-reads localStorage
// before refreshing and is reuse-grace-protected against this path, so the
// dangerous interleaving — the uncoordinated manual rotators — is gone.

import { supabase, AUTH_TIMEOUT_MS } from './supabase'
import { readStoredSession } from './offlineSession'

const SUPABASE_URL      = import.meta.env.VITE_SUPABASE_URL      as string
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string
const STORAGE_KEY = (() => {
  try { return `sb-${new URL(SUPABASE_URL).host.split('.')[0]}-auth-token` } catch { return '' }
})()

export interface RefreshedSession {
  access_token:  string
  refresh_token: string
  expires_at:    number
}

export type RefreshResult =
  | { ok: true;  session: RefreshedSession; rotated: boolean }
  | { ok: false; reason: 'no-session' | 'revoked' | 'network' | 'bad-response' }

let inFlight: Promise<RefreshResult> | null = null

/**
 * Single-flight, rotation-guarded refresh. Concurrent callers (watchdog,
 * anon-guard, switcher, support/platform view) all share one in-flight POST so
 * the one-time refresh_token is never replayed.
 */
export function refreshSession(): Promise<RefreshResult> {
  if (!inFlight) inFlight = doRefresh().finally(() => { inFlight = null })
  return inFlight
}

/**
 * For flows that just changed a server-side claim source (tenant switch →
 * preferred_tenant_id; platform/support view → app_metadata) and need a JWT
 * that reflects it: wait out any in-flight refresh (it predates the write, so
 * its JWT lacks the new claim), THEN run a guaranteed-fresh single-flight one.
 * The caller still verifies the claim and may retry — a refresh that started in
 * the gap is also post-write, so at worst the caller loops once more.
 */
export async function refreshSessionAfterMetadataChange(): Promise<RefreshResult> {
  if (inFlight) { try { await inFlight } catch { /* its failure is ours to re-run */ } }
  return refreshSession()
}

// The single auth-token rotation: token-source → rotation guard → bounded fetch
// → status triage → JSON guard → persist → in-memory sync. This is the ONE
// place that complexity now lives — it absorbs what used to be duplicated across
// sessionWatchdog (previously suppressed), the tenant switcher, platformView and
// SupportViewBanner, so total complexity across the codebase DROPS. fallow's
// new-only gate scores this consolidated function over threshold; the sequence
// is irreducible (each branch is a documented fail-closed step) and centralising
// it here is the point of the change, so suppress rather than re-scatter it.
// fallow-ignore-next-line complexity
async function doRefresh(): Promise<RefreshResult> {
  const refreshToken = readStoredSession()?.refresh_token
  if (!refreshToken) return { ok: false, reason: 'no-session' }

  // Rotation guard: re-read just before send (synchronous — nothing interleaves
  // between here and the POST). If another context already rotated the stored
  // token, ADOPT it rather than replay a consumed one (replay = family revoke).
  const guardSess = readStoredSession()
  if (guardSess?.refresh_token && guardSess.refresh_token !== refreshToken) {
    if (guardSess.access_token) {
      return { ok: true, rotated: false, session: {
        access_token:  guardSess.access_token,
        refresh_token: guardSess.refresh_token,
        expires_at:    guardSess.expires_at ?? 0,
      } }
    }
    return { ok: false, reason: 'no-session' }
  }

  // Bounded raw fetch — NOT the supabase client (whose getSession would queue
  // behind the very lock we may be recovering). Abort-cap so a dead socket can't
  // hang the sole refresh path. AUTH_TIMEOUT_MS shared with the client's net.
  let resp: Response
  const ctrl  = typeof AbortController !== 'undefined' ? new AbortController() : null
  const timer = ctrl
    ? setTimeout(() => ctrl.abort(new DOMException('auth refresh timed out', 'AbortError')), AUTH_TIMEOUT_MS)
    : null
  try {
    resp = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', apikey: SUPABASE_ANON_KEY },
      body:    JSON.stringify({ refresh_token: refreshToken }),
      signal:  ctrl?.signal,
    })
  } catch {
    return { ok: false, reason: 'network' }   // network / abort — session left intact
  } finally {
    if (timer) clearTimeout(timer)
  }

  if (!resp.ok) {
    // 400/401/403 = GoTrue rejected the token (expired / revoked / reuse-detected)
    // — a definitive revocation. Everything else (429, 5xx, captive portals) is
    // infrastructure; leave the session and let the caller retry.
    if (resp.status === 400 || resp.status === 401 || resp.status === 403) return { ok: false, reason: 'revoked' }
    return { ok: false, reason: 'network' }
  }

  let tokens: { access_token?: string; refresh_token?: string; token_type?: string; expires_in?: number; expires_at?: number; user?: unknown }
  try { tokens = await resp.json() }
  catch { return { ok: false, reason: 'bad-response' } }   // 200 non-JSON = captive portal
  if (!tokens.access_token || !tokens.refresh_token) return { ok: false, reason: 'bad-response' }

  // Persist in supabase-js v2 storage shape so a hard reload rehydrates the new
  // tokens (the source of truth the rotation guard above re-reads).
  const expires_at = tokens.expires_at ?? Math.floor(Date.now() / 1000) + (tokens.expires_in ?? 3600)
  try {
    if (STORAGE_KEY) window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
      access_token:  tokens.access_token,
      token_type:    tokens.token_type ?? 'bearer',
      expires_in:    tokens.expires_in ?? 3600,
      expires_at,
      refresh_token: tokens.refresh_token,
      user:          tokens.user ?? null,
    }))
  } catch (e) { console.warn('authRefresh: localStorage write failed', e) }

  // Best-effort in-memory sync so mounted subscribers + onAuthStateChange see
  // the new tokens. setSession goes through auth-js's lock; race a 3s cap so a
  // stuck lock can't block the coordinator.
  await Promise.race([
    supabase.auth.setSession({ access_token: tokens.access_token, refresh_token: tokens.refresh_token }).catch(() => {}),
    new Promise<void>(r => setTimeout(r, 3000)),
  ])

  return { ok: true, rotated: true, session: { access_token: tokens.access_token, refresh_token: tokens.refresh_token, expires_at } }
}
